import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SUPPORTED_HARDWARE,
  devEuiSchema,
  findHardware,
  hardwareIdSchema,
  telemetryProviderSchema,
  type CreateDeviceRequest,
} from "@/contracts";
import { appKeySchema, joinEuiSchema } from "@/contracts/devices/lorawan";
import { z } from "zod";

import {
  assetOptionsQuery,
  createDeviceMutation,
  featuresQuery,
  isHardwareEnabledInMap,
  organizationOptionsQuery,
  siteOptionsQuery,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardBody } from "@/components/ui/card";
import { SelectField, TextField } from "@/components/ui/field";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/ui/page";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { HardwareSelectField } from "./hardware-select-field";

const newFormFields = z.object({
  devEui: devEuiSchema,
  hardwareId: hardwareIdSchema,
  supplier: z.string().trim().max(60),
  provider: telemetryProviderSchema,
  organizationId: z.string(),
  assetId: z.string(),
  siteId: z.string(),
  loraApplicationId: z.string().trim().max(120),
  loraDeviceId: z.string().trim().max(120),
  joinEui: z.string().trim(),
  appKey: z.string().trim(),
});

// Messages are i18n keys, translated where the error is shown.
const newFormSchema = newFormFields.superRefine((values, context) => {
  if (values.provider !== "SIMULATOR") {
    if (values.appKey === "") {
      context.addIssue({ code: "custom", path: ["appKey"], message: "errorAppKeyRequired" });
    } else if (!appKeySchema.safeParse(values.appKey).success) {
      context.addIssue({ code: "custom", path: ["appKey"], message: "errorAppKeyFormat" });
    }
  }

  if (values.joinEui !== "" && !joinEuiSchema.safeParse(values.joinEui).success) {
    context.addIssue({ code: "custom", path: ["joinEui"], message: "errorJoinEuiFormat" });
  }
});

type NewFormValues = z.infer<typeof newFormSchema>;

const PROVIDER_LABELS: Record<z.infer<typeof telemetryProviderSchema>, string> = {
  THE_THINGS_STACK: "The Things Stack",
  CHIRPSTACK: "ChirpStack",
  SIMULATOR: "Simulator",
};

/**
 * Deliberately unselected, even though there is one option today.
 *
 * ADR-045 forbade defaulting the hardware, so that no unlabelled device ends
 * up recorded as the model the pilot happened to begin with. Preselecting the
 * only entry would reintroduce exactly that, one supported model from now.
 */
const NO_HARDWARE_CHOSEN = "";

export function DeviceNewPage() {
  const { t } = useTranslation("devices");
  const { t: tCommon } = useTranslation("common");
  const { t: tPages } = useTranslation("pages");
  const client = useApiClient();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // A super admin belongs to no tenant, so the API makes them name one.
  const isSuperAdmin = user?.role === "SUPER_ADMIN";
  const organizations = useQuery({ ...organizationOptionsQuery(client), enabled: isSuperAdmin });

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<NewFormValues>({
    resolver: zodResolver(newFormSchema),
    defaultValues: {
      devEui: "",
      hardwareId: NO_HARDWARE_CHOSEN as NewFormValues["hardwareId"],
      supplier: "",
      provider: "THE_THINGS_STACK",
      organizationId: "",
      assetId: "",
      siteId: "",
      loraApplicationId: "",
      loraDeviceId: "",
      joinEui: "",
      appKey: "",
    },
  });

  // `useWatch` rather than `watch()`: it yields the value itself, which the
  // React compiler can reason about, instead of a function it has to skip.
  const selectedOrganization = useWatch({ control, name: "organizationId" });
  // For a plain admin this stays undefined and the API scopes by session.
  const tenant = isSuperAdmin ? selectedOrganization || undefined : undefined;
  const tenantChosen = !isSuperAdmin || Boolean(selectedOrganization);

  const sites = useQuery({ ...siteOptionsQuery(client, tenant), enabled: tenantChosen });
  const assets = useQuery({ ...assetOptionsQuery(client, tenant), enabled: tenantChosen });
  const features = useQuery(featuresQuery(client));
  const enabledHardware = features.data?.enabledHardware;
  const hardwareOptions = SUPPORTED_HARDWARE.filter((hardware) =>
    isHardwareEnabledInMap(enabledHardware, hardware.id),
  );

  const selectedAssetId = useWatch({ control, name: "assetId" });
  const selectedAsset = assets.data?.data.find((asset) => asset.id === selectedAssetId);
  const hardwareId = useWatch({ control, name: "hardwareId" });
  const provider = useWatch({ control, name: "provider" });
  const siteId = useWatch({ control, name: "siteId" });
  const simulated = provider === "SIMULATOR";
  // Undefined until the flags load; treated as off, which only shows fields.
  const provisioning = features.data?.networkProvisioningEnabled === true;
  const vendorJoinEui = findHardware(hardwareId)?.lorawan.defaultJoinEui ?? null;
  const fieldError = (message: string | undefined) =>
    message === undefined ? {} : { error: t(message, { defaultValue: message }) };

  const mutation = useMutation({
    ...createDeviceMutation(client),
    onSuccess: async (device) => {
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      await navigate(`/devices/${device.id}`);
    },
  });

  const onSubmit = handleSubmit(async (values) => {
    if (isSuperAdmin && !values.organizationId) {
      setError("organizationId", { message: t("chooseOrgOwnsSensor") });

      return;
    }

    const body: CreateDeviceRequest = {
      devEui: values.devEui,
      // Model, manufacturer and measured type are the server's to derive from
      // this (ADR-050).
      hardwareId: values.hardwareId,
      provider: values.provider,
      ...(values.supplier ? { supplier: values.supplier } : {}),
      ...(values.organizationId ? { organizationId: values.organizationId } : {}),
      ...(values.assetId ? { assetId: values.assetId } : {}),
      // With an asset chosen the server derives the site (ADR-026).
      ...(!values.assetId && values.siteId ? { siteId: values.siteId } : {}),
      // Derived by the API when it registers on the Network Server (ADR-089).
      ...(!provisioning && values.loraApplicationId
        ? { loraApplicationId: values.loraApplicationId }
        : {}),
      ...(!provisioning && values.loraDeviceId ? { loraDeviceId: values.loraDeviceId } : {}),
      ...(!simulated && values.joinEui ? { joinEui: values.joinEui } : {}),
      // Sent once, to be forwarded to the Join Server; never kept here.
      ...(!simulated ? { appKey: values.appKey } : {}),
    };

    try {
      await mutation.mutateAsync(body);
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in newFormFields.shape) {
            setError(field as keyof NewFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <>
      <PageHeader title={t("register")} description={t("registerDescription")} />

      <Card>
        <CardBody>
          <form
            onSubmit={(event) => void onSubmit(event)}
            noValidate
            className="flex flex-col gap-4"
          >
            {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

            <div className="grid gap-4 sm:grid-cols-2">
              {isSuperAdmin ? (
                <SelectField
                  label={t("fieldOrganization")}
                  required
                  {...(errors.organizationId?.message === undefined
                    ? {}
                    : { error: errors.organizationId.message })}
                  value={selectedOrganization}
                  onChange={(value) =>
                    setValue("organizationId", value, { shouldValidate: true, shouldDirty: true })
                  }
                  data={[
                    { value: "", label: tPages("chooseOrganization") },
                    ...(organizations.data?.data ?? []).map((organization) => ({
                      value: organization.id,
                      label: organization.name,
                    })),
                  ]}
                />
              ) : null}

              <TextField
                label={t("fieldDevEui")}
                required
                placeholder="24E124725E100001"
                hint={t("hintDevEui")}
                className="font-mono"
                {...(errors.devEui?.message === undefined ? {} : { error: errors.devEui.message })}
                {...register("devEui")}
              />

              <HardwareSelectField
                label={t("fieldHardware")}
                required
                hint={t("hintHardware")}
                placeholder={t("chooseHardware")}
                {...(errors.hardwareId?.message === undefined
                  ? {}
                  : { error: errors.hardwareId.message })}
                value={hardwareId}
                onChange={(value) =>
                  setValue("hardwareId", value as NewFormValues["hardwareId"], {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                options={hardwareOptions}
              />

              <p className="-mt-2 text-sm text-ink-muted sm:col-span-2">
                {t("notListed")}{" "}
                <Link
                  to="/supported-devices"
                  className="text-brand-strong underline underline-offset-2"
                >
                  {t("seeSupported")}
                </Link>{" "}
                {t("decodeWarning")}
              </p>

              <TextField
                label={t("fieldSupplier")}
                hint={t("hintSupplier")}
                {...(errors.supplier?.message === undefined
                  ? {}
                  : { error: errors.supplier.message })}
                {...register("supplier")}
              />

              <SelectField
                label={t("fieldNetwork")}
                value={provider}
                onChange={(value) =>
                  setValue("provider", value as NewFormValues["provider"], {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                data={telemetryProviderSchema.options.map((option) => ({
                  value: option,
                  label: PROVIDER_LABELS[option],
                }))}
              />

              <SelectField
                label={t("fieldAsset")}
                hint={t("hintAsset")}
                disabled={!tenantChosen}
                value={selectedAssetId}
                onChange={(value) =>
                  setValue("assetId", value, { shouldValidate: true, shouldDirty: true })
                }
                data={[
                  { value: "", label: t("unassigned") },
                  ...(assets.data?.data ?? []).map((asset) => ({
                    value: asset.id,
                    label: `${asset.name} · ${asset.siteName}`,
                  })),
                ]}
              />

              <SelectField
                label={t("fieldSite")}
                disabled={!tenantChosen || selectedAssetId !== ""}
                hint={
                  selectedAsset
                    ? t("hintSiteFromAsset", { siteName: selectedAsset.siteName })
                    : t("hintSite")
                }
                value={siteId}
                onChange={(value) =>
                  setValue("siteId", value, { shouldValidate: true, shouldDirty: true })
                }
                data={[
                  { value: "", label: t("unassigned") },
                  ...(sites.data?.data ?? []).map((site) => ({
                    value: site.id,
                    label: site.name,
                  })),
                ]}
              />

              {provisioning ? null : (
                <>
                  <TextField
                    label={t("fieldLoraApp")}
                    hint={t("hintLoraApp")}
                    {...fieldError(errors.loraApplicationId?.message)}
                    {...register("loraApplicationId")}
                  />

                  <TextField
                    label={t("fieldLoraDevice")}
                    hint={tPages("optionalHint")}
                    {...fieldError(errors.loraDeviceId?.message)}
                    {...register("loraDeviceId")}
                  />
                </>
              )}

              {simulated ? null : (
                <>
                  <TextField
                    label={t("fieldJoinEui")}
                    placeholder={vendorJoinEui ?? "24E124C0002A0001"}
                    hint={
                      vendorJoinEui
                        ? t("hintJoinEuiDefault", { joinEui: vendorJoinEui })
                        : t("hintJoinEuiRequired")
                    }
                    className="font-mono"
                    {...fieldError(errors.joinEui?.message)}
                    {...register("joinEui")}
                  />

                  {/* A password field so the key is not left on screen or
                      offered back by the browser; it is never stored. */}
                  <TextField
                    label={t("fieldAppKey")}
                    required
                    type="password"
                    hint={t("hintAppKey")}
                    className="font-mono"
                    {...fieldError(errors.appKey?.message)}
                    {...register("appKey")}
                  />
                </>
              )}
            </div>

            {simulated ? null : (
              <Alert tone="info" title={t("loraKeysTitle")} announce={false}>
                {provisioning ? t("loraKeysProvisioned") : t("loraKeysManual")}{" "}
                {t("loraKeysDefaultWarning")}
              </Alert>
            )}

            <div className="flex gap-2">
              <Button type="submit" loading={isSubmitting || mutation.isPending}>
                {t("registerAction")}
              </Button>
              <Link
                to="/devices"
                className="inline-flex h-10 items-center rounded-md border border-border-subtle px-4 text-sm font-medium text-ink"
              >
                {tCommon("cancel")}
              </Link>
            </div>
          </form>
        </CardBody>
      </Card>
    </>
  );
}
